import { interactive } from '@/components/ui/interactive';
import { cn } from '@/lib/utils';
import type { KeyboardEvent, ReactNode } from 'react';

export interface FilterChipOption<Value extends string> {
  value: Value;
  label: string;
  /** Optional count rendered beside the label, as in the verification queue. */
  count?: number;
}

export interface FilterChipsProps<Value extends string> {
  /** Accessible name for the group — "filter by review status", not "filters". */
  label: string;
  options: ReadonlyArray<FilterChipOption<Value>>;
  value: Value;
  onChange: (value: Value) => void;
  /** Rendered at the inline end of the row — the prototype puts a result count there. */
  trailing?: ReactNode;
  className?: string;
}

/**
 * The status filter row above the verification, document and organization lists.
 *
 * Implemented as a radio group rather than a row of buttons: exactly one chip is active
 * at a time, which is what `role="radiogroup"` means, and it gets arrow-key navigation
 * and a single tab stop for free. A row of eight independent buttons would be eight tab
 * stops that announce nothing about being mutually exclusive.
 */
export function FilterChips<Value extends string>({
  label,
  options,
  value,
  onChange,
  trailing,
  className,
}: FilterChipsProps<Value>) {
  // The single tab stop is the active chip, so the arrow keys are how the others are
  // reached — a radio group's contract, and without it the inactive chips (tabIndex -1)
  // were unreachable from the keyboard. Arrows follow the reading direction, which in
  // the RTL console means ArrowLeft moves forward.
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const forward = ['ArrowDown', document.dir === 'rtl' ? 'ArrowLeft' : 'ArrowRight'];
    const back = ['ArrowUp', document.dir === 'rtl' ? 'ArrowRight' : 'ArrowLeft'];
    const step = forward.includes(event.key) ? 1 : back.includes(event.key) ? -1 : 0;
    if (step === 0) return;
    event.preventDefault();
    const current = options.findIndex((option) => option.value === value);
    const next = options[(current + step + options.length) % options.length];
    if (!next) return;
    onChange(next.value);
    const radios =
      event.currentTarget.parentElement?.querySelectorAll<HTMLElement>('[role="radio"]') ?? [];
    radios[options.indexOf(next)]?.focus();
  };

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <div role="radiogroup" aria-label={label} className="flex flex-wrap items-center gap-2">
        {options.map((option) => {
          const active = option.value === value;
          return (
            <button
              key={option.value}
              type="button"
              role="radio"
              aria-checked={active}
              tabIndex={active ? 0 : -1}
              onClick={() => onChange(option.value)}
              onKeyDown={onKeyDown}
              className={cn(
                interactive,
                'flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[13px] font-semibold',
                active
                  ? 'bg-navy text-white'
                  : 'border border-conc-100 bg-white text-conc-600 hover:border-navy',
              )}
            >
              {option.label}
              {option.count !== undefined && (
                <span
                  className={cn(
                    'num rounded-full px-1.5 py-0.5 text-[11px] font-bold',
                    active ? 'bg-white/20 text-white' : 'bg-conc-50 text-conc-500',
                  )}
                >
                  {option.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {trailing && <span className="ms-auto text-[12.5px] text-conc-500">{trailing}</span>}
    </div>
  );
}
